/**
 * Weather: a deterministic function of (seed, day, hour) so every client and the server
 * agree without anything on the wire. Three-hour slots get a hashed value each; the
 * current weather blends linearly between neighbouring slots, so rain builds up and
 * fades over about an hour. Rain makes roads slippery (physics reads `world.wet`),
 * fog shortens what the renderer shows.
 */
import { hash01 } from '../world/rng';

export interface Weather { rain: number; fog: number }
const SLOT = 3; // hours

function slotValue(seed: number, slot: number, kind: number): number { return hash01(seed * 7 + 1013 + kind, slot); }

/** rain 0..1, fog 0..1 at a world time. Rain in roughly a fifth of the slots, fog mostly at dawn. */
export function weatherAt(seed: number, day: number, hour: number): Weather {
  const t = day * 24 + hour;
  const s0 = Math.floor(t / SLOT), f = (t - s0 * SLOT) / SLOT;
  const r0 = rainOf(seed, s0), r1 = rainOf(seed, s0 + 1);
  const g0 = fogOf(seed, s0), g1 = fogOf(seed, s0 + 1);
  return { rain: r0 + (r1 - r0) * f, fog: g0 + (g1 - g0) * f };
}

function rainOf(seed: number, slot: number): number {
  const v = slotValue(seed, slot, 1);
  return v < 0.78 ? 0 : Math.min(1, (v - 0.78) / 0.18); // 0.78–0.96 → 0..1, above → downpour
}
function fogOf(seed: number, slot: number): number {
  const hour = ((slot * SLOT) % 24 + 24) % 24;
  const dawn = hour >= 3 && hour < 9 ? 1 : 0.25; // fog mostly around dawn
  const v = slotValue(seed, slot, 2);
  return v < 1 - 0.14 * dawn ? 0 : Math.min(1, (v - (1 - 0.14 * dawn)) / (0.14 * dawn));
}

export function weatherName(w: Weather): string {
  if (w.rain > 0.66) return 'downpour';
  if (w.rain > 0.15) return 'rain';
  if (w.fog > 0.4) return 'fog';
  if (w.fog > 0.12) return 'mist';
  return '';
}
